import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters");
    if (pw !== pw2) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await http.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in with your new password.");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-ink-50 px-6 py-16">
      <div className="max-w-md w-full bg-white border border-ink-100 p-10" data-testid="reset-password-page">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <img src="/logo-128.png" alt="LeadIQ Pro" width="40" height="40" className="w-10 h-10" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-base tracking-tight text-ink-900">
              LeadIQ Pro
            </div>
            <div className="text-[10px] uppercase tracking-widestx text-ink-400 font-mono">
              Set a new password
            </div>
          </div>
        </Link>

        <div className="w-12 h-12 grid place-items-center bg-ink-900 text-white mb-6">
          <ShieldCheck className="w-6 h-6" />
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink-900 leading-tight mb-3">
          Choose a new password.
        </h1>
        <p className="text-ink-500 text-sm mb-8 leading-relaxed">
          Make it at least 8 characters. Once you save, you'll be signed out everywhere else.
        </p>

        <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
          <div>
            <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
              New password
            </Label>
            <Input
              data-testid="reset-password-input"
              type="password"
              required
              minLength={8}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="••••••••"
              className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widestx font-semibold text-ink-500">
              Confirm new password
            </Label>
            <Input
              data-testid="reset-password-confirm-input"
              type="password"
              required
              minLength={8}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="••••••••"
              className="mt-2 rounded-sm border-ink-200 focus:border-brand focus-visible:ring-brand h-11"
            />
          </div>
          <Button
            data-testid="reset-password-submit-button"
            type="submit"
            disabled={busy || !pw || !pw2}
            className="w-full h-11 rounded-sm bg-brand hover:bg-brand-hover text-white font-medium tracking-wide group"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (<>Update password <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" /></>)}
          </Button>
        </form>

        <div className="mt-8 text-sm text-ink-500">
          <Link to="/login" className="text-brand hover:text-brand-hover font-medium underline-offset-4 hover:underline" data-testid="reset-back-to-login">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
